import React, { FC, PropsWithChildren, ReactNode, useId } from 'react';
import { LinkProps } from 'react-router-dom';

import { Reminder } from '../../generated/graphql';
import translate from '../../i18n/translate';
import { dateTimeFormatUi } from '../../utils/date/format';
import MetaContainerInline, {
  Props as PropsInline,
} from '../MetaContainerInline';
import MetaContainerPopup, { Props as PropsPopup } from '../MetaContainerPopup';

import { getIsSaveDisabled } from './helpers';

type Props = PropsWithChildren & {
  className?: string;
  dataDateDueCurrent: Date | null;
  dataDateDueSaved: Date | null;
  dataRemindersCurrent: Reminder[];
  dataRemindersSaved: Reminder[];
  inputCustom?: ReactNode;
  idForm?: string;
  isActive: boolean;
  linkToggleProps?: LinkProps;
  onClose?: () => void;
  requestDateDueRemove?: () => void;
  requestRemindersRemove: (position?: number) => void;
};

/**
 * Date due popup for the compose block
 *
 * @param props                        Props passed to the component
 * @param props.className              styled-components generated class name, needed for styling
 * @param props.children               Children to render inside the popup
 * @param props.dataDateDueCurrent     Current due date
 * @param props.dataDateDueSaved       Saved due date
 * @param props.dataRemindersCurrent   Current reminders
 * @param props.dataRemindersSaved     Saved reminders
 * @param props.inputCustom            If we want a custom component instead of plain input
 * @param props.idForm                 Form Id
 * @param props.isActive               Whether the component should be visible
 * @param props.linkToggleProps        Props for the toggle link
 * @param props.onClose                Callback to be invoked when the user clicks close button
 * @param props.requestDateDueRemove   Request that the current due date is removed (set to null)
 * @param props.requestRemindersRemove Request that the current reminder is removed (or all of the current reminders are removed)
 * @returns                            The component itself
 */
const MetaDateDue: FC<Props> = ({
  className,
  children,
  dataDateDueCurrent,
  dataDateDueSaved,
  dataRemindersCurrent,
  dataRemindersSaved,
  inputCustom,
  idForm = useId(),
  isActive,
  linkToggleProps = null,
  onClose,
  requestDateDueRemove = null,
  requestRemindersRemove,
}) => {
  /**
   * We need to check if currently selected date is equal to draft/saved date
   * so we can disable save button if they are equal.
   */
  const isSaveDisabled = getIsSaveDisabled(
    dataDateDueCurrent,
    dataDateDueSaved,
    dataRemindersCurrent,
    dataRemindersSaved,
  );

  const idHeading = useId();

  // Popup top buttons labels
  const labelAction = translate('GENERAL__REMOVE');
  const labelActionColor = 'var(--color-button-danger)';
  const labelConfirm = translate('GENERAL__SAVE');
  const labelClose = translate('GENERAL__CLOSE');

  const textPlaceholder = translate('DUE_DATE__PLACEHOLDER');
  const textHeading = textPlaceholder;

  const valueSaved =
    dataDateDueSaved === null ? '' : dateTimeFormatUi(dataDateDueSaved);

  // Props used for both inline and popup meta
  const propsCommon: PropsInline | PropsPopup = {
    action:
      requestDateDueRemove !== null && dataDateDueSaved !== null
        ? {
            color: labelActionColor,
            label: labelAction,
            /**
             * On click event handler
             */
            onClick: () => {
              requestDateDueRemove();
              requestRemindersRemove();
            },
          }
        : null,
    className,
    idForm,
    idHeading,
    isSaveDisabled,
    labelClose,
    labelConfirm,
    onClose,
    textHeading,
  };

  if (linkToggleProps === null) {
    return (
      <MetaContainerInline {...propsCommon}>{children}</MetaContainerInline>
    );
  }

  return (
    <MetaContainerPopup
      {...propsCommon}
      inputCustom={inputCustom}
      linkProps={linkToggleProps}
      placeholder={textPlaceholder}
      shouldHideSave={true}
      shouldResize={false}
      shouldShowPopup={isActive}
      value={valueSaved}
    >
      {children}
    </MetaContainerPopup>
  );
};

export default MetaDateDue;
